/* eslint-disable camelcase */
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid, Typography } from '@mui/material'
import Other from 'components/actions/Other'
import Input from 'components/forms/Input'
import NumberField from 'components/forms/NumberField'
import ProgressCard from 'components/card/ProgressCard'
import { push } from '@lagunovsky/redux-react-router'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import AgriAction from '../../../agriAdministration/actions/AgriAction'
import { LastUpdateCard } from '../../../components/card/LastUpdateCard'
import SimpleDatePicker from '../../../components/forms/SimpleDatePicker'
import ModalWarningDeleteSurvey from '../declaration/modals/ModalWarningDeleteSurvey'
import FormSurvey from '../panels/FormSurvey'
import ProgressSurveyChart from '../panels/ProgressSurveyChart'
import SurveyDeclarationsPanel from '../panels/SurveyDeclarationsPanel'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import { Warning } from '@mui/icons-material'
import { CardTitle } from 'components/card/NewCard'
import { SURVEY_STATUS } from 'agriAdministration/constants/AgriConstants'
import { darkBlue } from 'utils/constants/ColorTheme'

const SurveyDashboardApp = ({ params }) => {
    const [dataLoaded, setDataLoaded] = useState(false)
    const [newSurvey, setNewSurvey] = useState()
    const [openDuplicate, setOpenDuplicate] = useState(false)
    const [duplicateName, setDuplicateName] = useState()
    const [duplicateYear, setDuplicateYear] = useState()
    const [duplicateStartDate, setDuplicateStartDate] = useState()
    const [duplicateEndDate, setDuplicateEndDate] = useState()
    const [duplicateAdminEndDate, setDuplicateAdminEndDate] = useState()
    const [editMode, setEditMode] = useState(false)
    const [openDeleteWarningModal, setOpenDeleteWarningModal] = useState(false)
    const [openRecalculDialog, setOpenRecalculDialog] = useState(false)

    const {
        survey,
        exportFullSurvey,
        accountUser,
    } = useSelector(store => ({
        survey: store.AgriReducer.survey,
        exportFullSurvey: store.AgriReducer.exportFullSurvey,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const dispatch = useDispatch()

    const duplicateAction = () => {
        setOpenDuplicate(true)
        setDuplicateYear(survey?.year + 1 || null)
        setDuplicateName(`${survey?.name || ''} (${i18n.copy})`)
    }

    const onSave = () => {
        setDataLoaded(false)
        dispatch(AgriAction.updateSurvey(newSurvey)).then(() => {
            dispatch(AgriAction.fetchSurvey(newSurvey.idSurvey)).then(() => {
                setDataLoaded(true)
                setEditMode(false)
            })
        })
    }

    const onCancel = () => {
        setNewSurvey(survey)
        setEditMode(false)
    }

    useTitle(() => ([{
        title: i18n.planning,
        href: 'planning',
    }, {
        title: i18n.surveys,
        href: 'planning/surveys',
    }, {
        title: `${survey.name || ''} [${survey.year || ''}]`,
        href: `survey/${params.id}`,
    }, {
        title: i18n.dashboard,
        href: `survey/${params.id}/dashboard`,
    }]), [])

    useActions(() => {
        if (accountUser?.consultant === '1') return {}

        if (editMode) return { save: onSave, cancel: onCancel }

        const deleteAction = (accountUser?.isAdmin === '1'|| accountUser.metadata === '1') && survey.statusCode !== SURVEY_STATUS.PUBLISHED ? {
            deleteCheck: () => setOpenDeleteWarningModal(true),
        } : {}
        const recalculateAction = (accountUser?.isAdmin === '1' || accountUser.metadata === '1') && survey.statusCode !== SURVEY_STATUS.ARCHIVED ? {
            other: {
                other: (
                    <Other
                        className='clickable'
                        tooltip={i18n.recalculateVolumesPrevi}
                        icon='sync'
                        onClick={() => setOpenRecalculDialog(true)}
                    />
                ),
            },
        } : {}

        return {
            edit: () => setEditMode(true),
            duplicate: duplicateAction,
            ...recalculateAction,
            ...deleteAction,
        }
    }, [editMode, newSurvey])

    useEffect(() => {
        dispatch(AgriAction.fetchSurvey(params.id)).then(() => {
            dispatch(AgriAction.fetchSurveyAllExportData(params.id)).then(() => {
                setNewSurvey(survey)
                setDataLoaded(true)
            })
        })
    }, [])

    const onCloseDuplicateDialog = () => {
        setOpenDuplicate(false)
        setDuplicateYear(null)
        setDuplicateName(null)
    }

    const onDuplicate = () => {
        if (duplicateYear && duplicateStartDate && duplicateEndDate && duplicateAdminEndDate) {
            setDataLoaded(false)
            dispatch(AgriAction.duplicateSurvey({
                idSurvey: survey.idSurvey,
                newSurveyYear: duplicateYear,
                newSurveyName: duplicateName,
                startDate: duplicateStartDate,
                endDate: duplicateEndDate,
                adminEndDate: duplicateAdminEndDate,
            })).then(json => {
                onCloseDuplicateDialog()
                dispatch(push(`/survey/${json.newIdSurvey}`))
            })
        } else {
            dispatch(ToastrAction.error(i18n.pleaseCompleteAllField))
        }
    }

    const onDeleteSurvey = () => {
        setDataLoaded(false)
        dispatch(AgriAction.deleteSurvey(survey.idSurvey)).then(() => {
            dispatch(push('/planning/surveys'))
        })
    }

    const getModalDuplicate = () => {
        return (
            <Dialog
                fullWidth
                open={openDuplicate}
                maxWidth='md'
            >
                <DefaultDialogTitle
                    title={i18n.duplicateSurvey}
                    onClose={onCloseDuplicateDialog}
                />
                <DialogContent>
                    <Grid container spacing={1}>
                        <Grid size={6}>
                            <Input
                                title={i18n.name}
                                value={duplicateName}
                                onChange={value => setDuplicateName(value) }
                                obligatory
                            />
                        </Grid>
                        <Grid size={6}>
                            <NumberField
                                title={i18n.year}
                                value={duplicateYear}
                                onChange={value => setDuplicateYear(value)}
                                obligatory
                            />
                        </Grid>
                        <Grid size={6}>
                            <SimpleDatePicker
                                label={i18n.startDate}
                                value={duplicateStartDate}
                                onChange={value => setDuplicateStartDate(value)}
                                max={duplicateEndDate}
                                obligatory
                            />
                        </Grid>
                        <Grid size={6}>
                            <SimpleDatePicker
                                label={i18n.endDate}
                                value={duplicateEndDate}
                                onChange={value => setDuplicateEndDate(value)}
                                min={duplicateStartDate}
                                obligatory
                            />
                        </Grid>
                        <Grid size={6}>
                            <SimpleDatePicker
                                label={i18n.adminEndDate}
                                value={duplicateAdminEndDate}
                                onChange={value => setDuplicateAdminEndDate(value)}
                                obligatory
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onDuplicate} variant='contained' color='primary'>
                        {i18n.duplicate}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    const getModalWarningRecalcul = () => (
        <Dialog
            fullWidth
            maxWidth='md'
            open={openRecalculDialog}
        >
            <DefaultDialogTitle
                title={(
                    <Grid container spacing={1} alignItems='center'>
                        <Warning style={{ fontSize: '2.5rem' }}/>
                        {i18n.recalculateVolumesPrevi}
                    </Grid>
                )}
                onClose={() => setOpenRecalculDialog(false)}
            />
            <DialogContent>
                <Typography sx={{ fontWeight: 'bold' }}>
                    {i18n.sureRecalculVolumes}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
                <div />
                <Typography variant={'h5'} sx={{ color: 'red', fontWeight: 'bold' }}>{i18n.irreversibleAction}</Typography>
                <Button
                    variant='contained'
                    className='red'
                    style={{ color: 'white' }}
                    onClick={() => dispatch(AgriAction.recalculateVolumesSurvey(survey.idSurvey)).then(() => setOpenRecalculDialog(false))}
                >
                    {i18n.confirm}
                </Button>
            </DialogActions>
        </Dialog>
    )

    return (
        <Grid container size={12}>
            {dataLoaded ? (
                <Grid container size={12} spacing={1} sx={{ padding: 1 }}>
                    <Grid container justifyContent='stretch'>
                        <Grid size={9}>
                            <Card>
                                <CardTitle title={i18n.generalInfos} color={darkBlue} />
                                <CardContent>
                                    <FormSurvey
                                        survey={newSurvey}
                                        readMode={!editMode}
                                        onChangeSurvey={obj => setNewSurvey({ ...newSurvey, ...obj })}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={3} container direction='column' spacing={1}>
                            <LastUpdateCard
                                element={newSurvey}
                                withHour
                                round
                            />
                            <Grid>
                                <Card>
                                    <CardTitle title={i18n.declarationsProgress} color={darkBlue} />
                                    <CardContent>
                                        <ProgressSurveyChart
                                            survey={newSurvey}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={12}>
                        <SurveyDeclarationsPanel
                            readMode={!editMode}
                            newSurvey={newSurvey}
                            exportFullSurvey={exportFullSurvey}
                            onChange={value => {
                                setNewSurvey(prev => ({ ...prev, ...value }))
                            }}
                            actualiseSelfAssign={(id, links) => {
                                dispatch(AgriAction.fetchSurvey(id)).then(() => {
                                    setNewSurvey({ ...newSurvey, link_declarations: links })
                                })
                            }}
                            params={params}
                        />
                    </Grid>
                </Grid>
            ) : (
                <Grid size={12} sx={{ padding: 1 }}>
                    <ProgressCard indeterminate round />
                </Grid>
            )}
            {openDuplicate && getModalDuplicate()}
            {openDeleteWarningModal && (
                <ModalWarningDeleteSurvey
                    open={openDeleteWarningModal}
                    onCancel={() => setOpenDeleteWarningModal(false)}
                    onDelete={onDeleteSurvey}
                />
            )}
            {getModalWarningRecalcul()}
        </Grid>
    )
}

SurveyDashboardApp.propTypes = {
    params: PropTypes.shape({
        id: PropTypes.string,
    }),
}

export default SurveyDashboardApp
